import { useEffect, useState } from "react";
import { trim } from "lodash";
import moment from "moment";
import { gql } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  makeStyles
} from "@material-ui/core";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useSetConsultantNextContactDateMutation,
  useGetConsultantNextContactDateLazyQuery
} from "@bonliva-crm/graphql";
import {
  DatePickerController,
  RadioButtonGroup,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { useLocale } from "@hooks";
import { FormData, schema } from "./utils";
import { NextContactDateReason, ConsultantPipelineStatus } from "@bonliva-crm/shared";
import { localToImplicit } from "@bonliva-crm/common";

type Props = {
  open: boolean;
  consultantId: string;
  onClose: () => void;
};

// Used by the mutation's cache update to read the card's current columnId
// before deciding whether a cache.modify is needed.
const COLUMN_ID_FRAGMENT = gql`
  fragment _ColumnIdOnly on ConsultantPipelineCard {
    columnId
  }
`;

export function SetConsultantRelevantLaterDialog(props: Props) {
  const { locale } = useLocale();
  const [customReason, setCustomReason] = useState<string>("");
  const snack = useSnackbarsPipeline();
  const classes = useStyles();
  const { control, getValues, reset, formState } = useForm<FormData>({
    defaultValues: {
      nextContactDate: null,
      reason: null
    },
    resolver: yupResolver(schema)
  });

  const [mutate] = useSetConsultantNextContactDateMutation();

  const [load] = useGetConsultantNextContactDateLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      reset({
        nextContactDate: data.consultantById.nextContactDate?.date
          ? moment(data.consultantById.nextContactDate?.date).toDate()
          : null,
        reason: data.consultantById.nextContactDate?.reason || null
      });
    }
  });

  useEffect(() => {
    if (props.open) {
      load({
        variables: {
          consultantId: props.consultantId
        }
      });
    }
  }, [props.open]);

  const submit = () => {
    const values = getValues();
    const date = values.nextContactDate ? localToImplicit(values.nextContactDate) : null;
    const reason = values.reason === "Other" ? customReason : values.reason;

    return mutate({
      variables: {
        consultantId: props.consultantId,
        date,
        reason
      },
      update: (cache) => {
        // Sync the Apollo cache so the next kanbanCards re-emit shows the
        // card in "Aktuell längre fram" with the new nextContactDate, so
        // it renders yellow with the date visible immediately. Writing
        // the Consultant entity here is what makes the card colour update
        // — `getBgColor` is driven by `consultant.nextContactDate.date`
        // per CRMDEV-2644.
        //
        // We only update Card.columnId when it's actually changing
        // (avoids a wasted broadcast for menu-edits where the card is
        // already in RelevantInTheFuture). The Consultant write always
        // fires because at least nextContactDate is changing.
        //
        // It's now safe to broadcast Consultant updates from here even
        // though the backend's notifyOnConsultantUpdated handler will
        // also publish a false-positive KanbanCardDeletedEvent for the
        // originator — useCardDeletedSubscription now routes through
        // patchSingleCard which verifies the card actually disappeared
        // server-side before removing it from local state.
        const cardId = cache.identify({
          __typename: "ConsultantPipelineCard",
          _id: props.consultantId
        });
        if (cardId) {
          const existing = cache.readFragment<{ columnId?: string }>({
            id: cardId,
            fragment: COLUMN_ID_FRAGMENT
          });
          if (existing?.columnId !== ConsultantPipelineStatus.RelevantInTheFuture) {
            cache.modify({
              id: cardId,
              fields: {
                columnId: () => ConsultantPipelineStatus.RelevantInTheFuture
              }
            });
          }
        }
        const consultantId = cache.identify({
          __typename: "Consultant",
          _id: props.consultantId
        });
        if (consultantId) {
          cache.modify({
            id: consultantId,
            fields: {
              pipelineStatus: () => ConsultantPipelineStatus.RelevantInTheFuture,
              nextContactDate: () => ({
                __typename: "ConsultantNextContactDate",
                date,
                reason
              })
            }
          });
        }
      }
    })
      .then(() => {
        snack.showSuccess("Updated");
        props.onClose();
      })
      .catch((e) => {
        snack.showError("Error");
        console.error(e);
      });
  };

  const canSubmit = formState.isValid && !formState.isSubmitting;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      classes={{
        paper: classes.dialogRoot
      }}
    >
      <DialogTitle>{locale.enterCurrentFurtherForward}</DialogTitle>

      <DialogContent>
        <Box>
          <DatePickerController
            name="nextContactDate"
            control={control}
            DatePickerProps={{
              label: locale.NextContact
            }}
          />
        </Box>

        <Box mt={2}>
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <RadioButtonGroup
                label={locale.reason}
                options={[
                  {
                    label: "Arbetar för konkurrent",
                    value: NextContactDateReason.WorksForCompetitor
                  },
                  {
                    label: "Fast tjänst",
                    value: NextContactDateReason.PermanentEmployment
                  },
                  {
                    value: "Other",
                    label: (
                      <TextField
                        onFocus={(e) => field.onChange("Other")}
                        onBlur={(e) => field.onChange(trim(customReason))}
                        error={field.value === "Other" && !trim(customReason)}
                        onChange={(e) => setCustomReason(e.target.value)}
                        placeholder={locale.customReason}
                      />
                    )
                  }
                ]}
                {...field}
              />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="primary" onClick={() => props.onClose()}>
          {locale.Cancel}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => submit()}
          disabled={!canSubmit}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles({
  dialogRoot: {
    overflowY: "visible"
  }
});
